/**
 * Onboarding Screen 3: Ask Swirl Preview
 * Demonstrate AI conversation with typing animation
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Sparkles, Clock, Target, TrendingUp, Calendar } from 'lucide-react';
import { sampleAskSwirlData } from '../data/sampleData';

interface AskSwirlPreviewProps {
  onNext: () => void;
}

export const AskSwirlPreview: React.FC<AskSwirlPreviewProps> = ({ onNext }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [showInsights, setShowInsights] = useState(false);
  const fullText = sampleAskSwirlData.aiResponse;

  useEffect(() => {
    let index = 0;
    const interval = setInterval(() => {
      if (index <= fullText.length) {
        setDisplayedText(fullText.slice(0, index));
        index++;
      } else {
        clearInterval(interval);
        // Show insights after typing completes
        setTimeout(() => setShowInsights(true), 300);
      }
    }, 20); // Typing speed

    return () => clearInterval(interval);
  }, [fullText]);

  const insightIcons = [Clock, Target, Calendar, TrendingUp];

  return (
    <div className="flex flex-col h-full bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 p-8 overflow-y-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8 text-center"
      >
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm mb-4">
          <MessageCircle className="w-5 h-5 text-purple-500" />
          <span className="font-semibold text-gray-900">Ask Swirl</span>
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">
          Your AI thinking partner
        </h2>
        <p className="text-gray-600">
          Have natural conversations and get personalized insights
        </p>
      </motion.div>

      {/* Conversation Container */}
      <div className="flex-1 max-w-3xl mx-auto w-full space-y-4">
        {/* User Message */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="flex justify-end"
        >
          <div className="max-w-md bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-2xl rounded-tr-sm px-5 py-3 shadow-lg">
            <p className="font-medium">
              {sampleAskSwirlData.userMessage}
            </p>
          </div>
        </motion.div>

        {/* AI Response with Typing Effect */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5 }}
          className="flex justify-start"
        >
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <span className="text-sm font-semibold text-gray-900">Swirl AI</span>
            </div>
            <div className="bg-white rounded-2xl rounded-tl-sm px-5 py-4 shadow-lg border border-gray-100">
              <p className="text-gray-800 leading-relaxed">
                {displayedText}
                {displayedText.length < fullText.length && (
                  <motion.span
                    animate={{ opacity: [1, 0] }}
                    transition={{ duration: 0.5, repeat: Infinity }}
                    className="inline-block w-1 h-5 bg-purple-500 ml-1"
                  />
                )}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Extracted Insights */}
        <AnimatePresence>
          {showInsights && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="pt-6"
            >
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-4 h-4 text-purple-500" />
                <span className="text-sm font-semibold text-gray-700">Key Insights Extracted</span>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                {sampleAskSwirlData.insights.map((insight, index) => {
                  const Icon = insightIcons[index] || Clock;
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-white rounded-xl p-4 border border-purple-100 shadow-sm"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-4 h-4 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-600 mb-1">{insight.label}</p>
                          <p className="text-sm font-semibold text-gray-900">{insight.value}</p>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Next Button */}
      <AnimatePresence>
        {showInsights && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-8 flex justify-center"
          >
            <button
              onClick={onNext}
              className="px-8 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all hover:scale-105"
            >
              Next
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};





